import Head from 'next/head';
import { useRouter } from 'next/router';
import { memo } from 'react';

import type { WWQueryProps } from '../context/RegistryContext';
import { imageSourceBuilder } from './Picture';

function PreloadImages({ data }: WWQueryProps) {
  const { query } = useRouter();

  if (
    !data ||
    data.weddingWebsiteResult?.__typename !== 'WWS_WeddingWebsiteV2'
  ) {
    return null;
  }

  const { route = [], useDefaultCrop = false } = query || {};

  const isHomePage = !route.length;

  const hero: string | null | undefined = isHomePage
    ? data?.weddingWebsiteResult.coverPhoto?.path
    : undefined;

  if (!hero) {
    return null;
  }

  const defaultCrop = { height: 768, width: 1024 };

  const smartCrop = useDefaultCrop ? defaultCrop : null;

  let defaultSmartCrop = '';

  if (smartCrop) {
    defaultSmartCrop = `${smartCrop?.width}.${smartCrop?.height}`;
  }

  const landscapeCrop =
    data?.weddingWebsiteResult.coverPhoto?.cropBox || undefined;
  const parsedRotation = Number(
    data?.weddingWebsiteResult.coverPhoto?.rotationAngle
  );
  const coverPhotoRotation = Number.isFinite(parsedRotation)
    ? parsedRotation
    : 0;

  function renderLink(src: string, pixel = 1) {
    if (!src) {
      return '';
    }

    const imageSrcSet = [480, 768, 1024, 1280].reduce(
      (previousValue, currentValue) =>
        `${previousValue}${previousValue ? ', ' : ''}${imageSourceBuilder(
          src,
          defaultSmartCrop,
          currentValue * pixel,
          landscapeCrop,
          undefined,
          coverPhotoRotation
        )} ${currentValue}w`,
      ''
    );

    // we want the lowest media width for href
    const href = imageSourceBuilder(
      src,
      defaultSmartCrop,
      480 * pixel,
      landscapeCrop,
      undefined,
      coverPhotoRotation
    );

    return (
      <link
        rel="preload"
        as="image"
        href={href}
        imageSrcSet={imageSrcSet}
        imageSizes="100vw"
        data-testid="preload-images"
      ></link>
    );
  }

  return (
    <Head>
      {renderLink(hero)}
      {renderLink(hero, 1.5)}
      {renderLink(hero, 2)}
    </Head>
  );
}

export default memo(PreloadImages);
